// ─── WOT Playable Topic Core (v2 proposal) ──────────────────────────
// Twenty topics per realm, written for a general audience.
//
// Why this exists alongside taxonomy.ts: the v1 taxonomy is a good
// *reference* tree (93 branches, 500+ subtopics) but it reads like a
// syllabus. Titles such as "UNIX Philosophy & Operating System Design"
// or "Borders Redrawn: Partition & Unification" describe a field of
// study, not something a player wants to tap. And in practice only four
// realms ever generated against it; the other six were generated with no
// topic at all, which produced 5,108 rows labelled "General X knowledge"
// and, measurably, the worst semantic repetition in the pool (ATLAS 21%,
// COSMOS 20%, FEAST 17% near-duplicate rate, versus 4-5% in the two
// realms that did use topics).
//
// So this list has one job: be the generation target for every realm, so
// no batch is ever produced topicless again.
//
// Naming rules, taken from the SOUNDWAVE branch of v1, which is the one
// part of the old tree that already reads right:
//   1. Lead with the hook, not the category. "Bronx Block Parties",
//      not "Hip-Hop History".
//   2. Only things a general adult has plausibly bumped into through
//      school, news, film or life.
//   3. Spread eras and cultures. A trivia realm that is 80% Western
//      loses most of the world.
//   4. Each topic must sustain 40-60 questions across all three
//      difficulties. Too narrow and the factory repeats itself, which is
//      exactly how the Schnitzel triplets happened.

export const TOPIC_CORE: Record<string, string[]> = {
  ARENA: [
    'World Cup Finals: Goals, Upsets & Heartbreak',
    'Olympic Torch, Ceremonies & Iconic Moments',
    'NBA Dynasties: Jordan, Kobe, LeBron',
    "Boxing's Greatest Nights",
    'Formula 1 Rivalries & Famous Crashes',
    'Tennis Grand Slam Epics',
    'Club Football Rivalries & Derby Days',
    'Sports Nicknames & Retired Numbers',
    'Sprints, Marathons & Broken Records',
    'Winter Olympics: Ice, Snow & Medals',
    "Cricket's Great Test Matches",
    'Wrestling & MMA: From Ring to Cage',
    'Sports Movies & the Real Stories Behind Them',
    'Stadiums Everyone Recognises',
    'Doping Scandals & Fallen Champions',
    'Underdogs & Miracle Comebacks',
    'Legendary Coaches & Their Methods',
    'Skate, Surf & Climb: Extreme Sports',
    "Golf's Majors & Famous Holes",
    'Esports: From Arcades to Arenas',
    'Rules of the Game: How Sports Actually Work',
    'Athletes Everyone Has Heard Of',
    'American Football: Super Bowls & Legends',  // 2026-08-21 genisletme
    'Baseball: The American Pastime',  // 2026-08-21 genisletme
    'Ice Hockey & the NHL',  // 2026-08-21 genisletme
    'Cycling: Grand Tours & Sprints',  // 2026-08-21 genisletme
  ],

  ATLAS: [
    'Capital Cities You Should Know',
    'Rivers That Built Civilisations',
    'Mountains, Peaks & Famous Climbs',
    'Islands: Paradise, Prison & Volcano',
    'Deserts & the People Who Cross Them',
    'Flags: Colours, Symbols & Stories',
    'Borders That Changed in Living Memory',
    'Cities Built on Water',
    'Wonders of the World, Old & New',
    'Oceans, Seas & Famous Straits',
    'Volcanoes & Earthquake Country',
    'Landmarks You Would Recognise Instantly',
    'Countries With More Than One Name',
    'Extremes: Coldest, Driest, Highest',
    'Great Migrations & Human Movement',
    'National Parks & Protected Wilderness',
    'Time Zones, the Equator & the Poles',
    'Lost Cities & Ancient Ruins',
    'Megacities & How They Grew',
    'Maps, Explorers & Famous Cartography Mistakes',
    'Famous Cities & What They Are Known For',
    'Animals, Plants & Where They Live',
    'Continents & Their Records',  // 2026-08-21 genisletme
    'Lakes & Waterfalls of the World',  // 2026-08-21 genisletme
    'Bridges, Canals & Great Crossings',  // 2026-08-21 genisletme
    'Weather & Climate Zones of Earth',  // 2026-08-21 genisletme
  ],

  CHRONICLES: [
    'Ancient Egypt: Pharaohs, Tombs & Curses',
    'Rome: Emperors, Legions & Collapse',
    'Where Greek Myth Meets Greek History',
    'Vikings: Raids, Ships & Sagas',
    'Knights, Castles & the Crusades',
    'The Silk Road: Traders, Spices & Ideas',
    'Ottoman Sultans & Imperial Istanbul',
    'Samurai, Shoguns & Feudal Japan',
    'Aztec, Maya & Inca',
    'Age of Sail: Explorers & Pirates',
    'French Revolution: Guillotine & Terror',
    'Napoleon: Rise, Empire, Exile',
    'The American Revolution & Its Myths',
    'Industrial Age: Steam, Coal & Crowded Cities',
    'World War I: Trenches & the Christmas Truce',
    'World War II: The Turning Points',
    'Cold War: Spies, Walls & Missiles',
    'Civil Rights Movements That Won',
    'Kings & Queens Who Changed Everything',
    'Assassinations That Shifted History',
    'Figures Everyone Has Heard Of',
    'Inventions & Firsts That Changed Daily Life',
    // ── Added when the v1 leftovers were audited ────────────────────
    // 522 CHRONICLES rows sat under v1 names with no home in v2. They were not
    // misfiled: v2 simply had no topic for China, India, Korea, Southeast Asia,
    // Africa, Persia, Mesopotamia, the Islamic world, Byzantium or Latin
    // America. The list above reads as a history of Europe and its neighbours,
    // which is a decision nobody made on purpose.
    'Mesopotamia: The First Cities',
    'Persia: Cyrus, Darius & the Royal Road',
    'Ancient China: Shang to Han',
    'Imperial China: Ming, Qing & the Last Emperor',
    'Ancient India: Maurya, Gupta & Ashoka',
    'Mughal India: Emperors & Architecture',
    'Korean Dynasties: Goryeo & Joseon',
    'Angkor, Srivijaya & Southeast Asia',
    'African Kingdoms: Mali, Ghana & Great Zimbabwe',
    'The Islamic Golden Age',
    'The Mongol Empire & the Pax Mongolica',
    'Byzantium: The Empire That Outlived Rome',
    'The Reformation & Europe\'s Religious Wars',
    'The Renaissance: Art, Money & Genius',
    'Latin America: Bolivar & Independence',
    'Empires End: Decolonisation & New Borders',
    'Between the Wars: Fascism, Crash & Unrest',
    'Treaties That Redrew the Map',
    'Meiji Japan: Samurai to Modern Empire',  // 2026-08-21: 21 sorusu Samurai konusundan tasindi
    'History of Education Systems',  // 2026-08-23: 18 sorusu taksonomi disinda kalmisti
  ],

  COSMOS: [
    'The Planets: Facts & Freak Weather',
    'Moon Landings & the Space Race',
    'Black Holes & What Falls In',
    'Stars: Birth, Death & Supernovas',
    'Mars: Rovers, Water & Colony Plans',
    'Telescopes: Hubble, Webb & What They Saw',
    'Astronauts & Life in Zero Gravity',
    'Comets, Asteroids & Impact Events',
    'The Sun: Flares, Eclipses & Cycles',
    'Galaxies & the Shape of the Universe',
    'Rockets: From the V-2 to Reusable Boosters',
    'Space Stations & the Longest Missions',
    'Dark Matter & Dark Energy',
    'Hunting Aliens: SETI & Exoplanets',
    'Space Disasters & Close Calls',
    'Constellations & Ancient Sky Maps',
    'Voyager & the Golden Record',
    'Space Agencies Around the World',
    'The Big Bang & Cosmic Beginnings',
    'Satellites, GPS & Space Junk',
    'The Everyday Sky: Day, Night, Seasons & Tides',
    'Space in Film & Popular Imagination',
    'Moons of the Solar System',  // 2026-08-21 genisletme
    'Space Records & Famous Firsts',  // 2026-08-21 genisletme
    'Private Space: SpaceX, Blue Origin & the New Race',  // 2026-08-21 genisletme
    'Cosmic Distances: Light Years & Scale',  // 2026-08-21 genisletme
  ],

  ENIGMA: [
    'Greek Gods & Their Grudges',
    'Norse Myth: Ragnarok & Tricksters',
    'The Egyptian Afterlife',
    'Disappearances Nobody Solved',
    'Cryptids: Bigfoot, Nessie & Friends',
    'Structures Nobody Can Fully Explain',
    'Curses, Omens & Superstitions Worldwide',
    'Secret Societies & Their Symbols',
    'Atlantis & Other Lost Lands',
    'Ghost Stories That Refuse to Die',
    'Japanese Yokai & Spirit Folklore',
    'Prophecies That Made History',
    'Codes & Ciphers Still Unbroken',
    'UFO Sightings & Official Files',
    "Alchemy & the Philosopher's Stone",
    'Hindu Epics: Ramayana & Mahabharata',
    'Celtic Folklore & the Fae',
    'Dreams, Omens & How Cultures Read Them',
    'Real Places With Dark Reputations',
    'Hoaxes That Fooled the World',
    'Monsters & Legends in Popular Culture',
    'Symbols, Signs & Lucky Charms',
    'Fairy Tales & Their Dark Originals',  // 2026-08-21 genisletme
    'Urban Legends of the Digital Age',  // 2026-08-21 genisletme
    'Mesoamerican Myths: Aztec & Maya',  // 2026-08-21 genisletme
    'Slavic Folklore: Baba Yaga & Beyond',  // 2026-08-21 genisletme
  ],

  EUREKA: [
    'Everyday Physics: Why Things Fall & Float',
    "The Periodic Table's Odd Characters",
    'The Human Body: Organs, Blood & Bone',
    'Evolution & the Fossils That Proved It',
    'Vaccines & the Diseases They Beat',
    'Einstein & the Relativity Revolution',
    'Electricity: Edison, Tesla & the Grid',
    'DNA, Genes & What You Inherit',
    'Weather: Storms, Fronts & Forecasts',
    'The Deep Ocean & Its Creatures',
    'Dinosaurs: Kings, Feathers & Extinction',
    'Chemistry in the Kitchen',
    'Great Accidental Discoveries',
    'Light, Colour & How Eyes Work',
    'Nobel Prizes & the People Behind Them',
    "Medicine's Turning Points",
    'The Animal Kingdom: Senses, Records & Oddities',
    'Nuclear: Bombs, Reactors & Radiation',
    'Famous Maths Puzzles & Proofs',
    'Rocks, Volcanoes & the Restless Earth',
    'Everyday Science: Why Things Work That Way',
    'Plants, Trees & the Green World',
    // ── Added when the v1 leftovers were audited ────────────────────
    // 410 EUREKA rows had no home either. The gap here is engineering and the
    // physical sciences below the headline: flight, structures, thermodynamics,
    // organic chemistry, particle physics, telecommunications, printing. The
    // list above covers what science discovered and skips how anything was
    // built.
    'Flight: From Kitty Hawk to the Jet Age',
    'Engineering: Bridges, Towers & Tunnels',
    'Particle Physics & the Standard Model',
    'Telecommunications: Telegraph to Fibre',
    'Printing & How Information Spread',
    'Ecology & How Ecosystems Hold Together',
    'Organic Chemistry & Carbon',
    'Pseudoscience & How to Spot It',
    'Thermodynamics: Heat, Work & Entropy',
    'Plastics & the Polymer Age',
    'Rare Earths & Strategic Materials',
    'Ethics & Scandals in Science',
    'Rivalries & Priority Fights in Science',
    'Medical Imaging: Seeing Inside',
    'Women Who Changed Science',
    'Microbes: Bacteria, Viruses & Us',
  ],

  FEAST: [
    'National Dishes & Where They Come From',
    'Street Food Around the World',
    'Cheese: Regions, Moulds & Rules',
    'Coffee: Beans, Brewing & Ritual',
    'Spices That Started Wars',
    'Bread in Every Culture',
    'Desserts With a Story',
    'Wine, Beer & Fermentation',
    'Cooking Techniques Explained',
    'Kitchen Tools & Their Origins',
    'Chilli Peppers & Heat Scales',
    'Sushi, Ramen & the Japanese Table',
    'Pasta Shapes & Italian Regions',
    'The Turkish & Middle Eastern Table',
    'Indian Curries & Regional Spice',
    'Chinese Regional Cooking',
    'Mexican Food Beyond Tacos',
    'Famous Chefs & Restaurant Culture',
    'Food Myths & Nutrition Facts',
    'Fast Food Empires & How They Grew',
    'Everyday Food: Salads, Sauces & Sides',
    'Fruit, Vegetables & Where They Grow',
    'Breakfasts of the World',  // 2026-08-21 genisletme
    'French Cuisine: From Bistro to Haute',  // 2026-08-21 genisletme
    'The Korean Table: Kimchi to BBQ',  // 2026-08-21 genisletme
    'Southeast Asian Flavours: Thai & Vietnamese',  // 2026-08-21 genisletme
  ],

  NEXUS: [
    'The Computers That Started It All',
    'Internet History: ARPANET to the Web',
    'Smartphone Wars: iPhone vs Android',
    'Game Consoles Through the Years',
    'Arcade Classics & Their Creators',
    'Social Platforms: Rise & Fall',
    'Hackers, Viruses & Famous Breaches',
    'AI: From Chess Machines to Chatbots',
    'Programming Languages & Who Made Them',
    'Tech Founders & Garage Startups',
    'Passwords, Encryption & Staying Safe',
    'Memes & Internet Culture',
    'Streaming & the Death of the Disc',
    'Robots: Factory Arms to Humanoids',
    'Crypto, Blockchain & Digital Money',
    'Phones Before Smartphones',
    'Screens: From CRT to OLED',
    'Space Tech That Ended Up in Your Pocket',
    'Famous Product Flops',
    'Open Source & the Free Software Movement',
    'Tech Words & Symbols Everyone Uses',
    'Digital Life: Apps, Accounts & Devices',
    'How the Internet Reaches You: Wi-Fi, Cables & 5G',  // 2026-08-21 genisletme
    'PC Gaming & Online Worlds',  // 2026-08-21 genisletme
    'Big Tech Rivalries & Acquisitions',  // 2026-08-21 genisletme
    'Wearables, Smart Homes & Gadgets',  // 2026-08-21 genisletme
  ],

  SOUNDWAVE: [
    'Motown Records & the Soul Sound',
    'Hip-Hop Origins: Bronx Block Parties',
    'The Beatles & the British Invasion',
    'Punk Rock & the DIY Ethic',
    'Grunge & 1990s Seattle',
    'Disco, Dance Floors & Studio 54',
    'Reggae, Ska & Jamaican Sound Systems',
    'Classical Giants: Bach to Beethoven',
    'Jazz: New Orleans to Bebop',
    'Blues Origins: Mississippi Delta & Chicago',  // 2026-08-23: 10 sorusu taksonomi disinda kalmisti
    'Music Video as Art Form (MTV Era)',
    'Protest Songs & Political Music',
    'Album Cover Art as Statement',
    'One-Hit Wonders & Where They Went',
    'Festivals: Woodstock to Glastonbury',
    'Genres Explained & How Instruments Work',
    'K-Pop & the Global Pop Machine',
    'Country, Folk & Americana Storytelling',
    'Electronic Music & the Rise of the DJ',
    'Soundtracks That Made the Film',
    'Voices: Ranges, Styles & Famous Singers',
    'Rock Bands: Line-ups, Albums & Legends',
    'Songs Everyone Knows the Words To',
    'Hip-Hop Now: 2000s to Today',  // 2026-08-21 genisletme
    'Pop Hits of the 2000s & 2010s',  // 2026-08-21 genisletme
    'Latin Music: Salsa to Reggaeton',  // 2026-08-21 genisletme
    'Eurovision & Song Contests',  // 2026-08-21 genisletme
  ],

  SPOTLIGHT: [
    'Oscar Winners & Famous Snubs',
    'Disney Animation Through the Decades',
    'Studio Ghibli & Anime Cinema',
    'Superhero Films & Their Comic Origins',
    'Horror Classics & What Scared Us',
    'James Bond: Actors, Cars & Villains',
    'Star Wars & Star Trek',
    'Directors With a Signature Style',
    'Movie Quotes Everyone Knows',
    'Practical Effects Before CGI',
    'Sitcoms That Defined a Decade',
    'Prestige TV & the Streaming Era',
    'Bollywood & Indian Cinema',
    'Classic Hollywood: Silents to the Golden Age',
    'Broadway Musicals & Their Songs',
    'Casting Stories & Roles Almost Taken',
    'Franchises & Box Office Records',
    'Documentaries That Changed Minds',
    'Comedy Legends & Their Timing',
    'Cult Films & Midnight Screenings',
    'Famous Films & Their Characters',
    'Theatre, Plays & Stage Classics',
    'Pixar, DreamWorks & Modern Animation',  // 2026-08-21 genisletme
    'Crime, Thrillers & Whodunits',  // 2026-08-21 genisletme
    'Romantic Comedies & Feel-Good Classics',  // 2026-08-21 genisletme
    'Fantasy Epics: Middle-earth to Westeros',  // 2026-08-21 genisletme
  ],
};

/**
 * Realms no longer carry the same number of topics. CHRONICLES and EUREKA were
 * extended once it became clear that v2 had no home for whole civilisations and
 * whole branches of engineering, and that 932 rows were sitting outside the
 * taxonomy as a result. Anything sizing a realm must ask that realm.
 */
export const topicCount = (realm: string) => (TOPIC_CORE[realm] ?? []).length;

/** @deprecated Was a flat 22 for every realm. Use topicCount(realm). */
export const TOPIC_CORE_SIZE = 22;

// ─── Difficulty budget per topic ────────────────────────────────────
//
// The problem this solves: some realms have a genuinely higher floor.
// The most famous fact in "Polymer Science" is less widely known than the
// most famous fact in "National Dishes", so a player who picks EUREKA
// feels punished for the choice. Left alone, realm choice becomes
// difficulty choice, players avoid the hard realms, and those realms die.
//
// The fix is NOT to swap hard topics for easy ones. Difficulty is a
// property of the question, not the topic: every topic has a famous
// surface and an obscure depth. "Cold War" can ask who built the Berlin
// Wall (easy) or when the Warsaw Pact dissolved (hard). Swapping topics
// loses good material and still does not stop the factory from diving
// deep, which is how we got here in the first place.
//
// So each topic instead carries a MIX it must generate to. Every realm
// gets the same tier counts, which means every realm ends up with the
// same overall difficulty curve no matter how specialised its subject is.

// ─── Coverage targets ───────────────────────────────────────────────
//
// The unit is the TOPIC, not the realm and not the pool. A realm total hides
// the thing that actually matters: a realm can hold 1,900 questions and still
// have topics with none, and the player who picks that topic sees the same
// questions every match.
//
// Sizing comes from repetition. A match is 10 questions, so a player who plays
// a realm twenty times has seen 200. The pool for that realm needs to be
// several times that before repeats become noticeable, and it has to hold up
// per difficulty band, since the game samples within a band rather than across
// the whole realm.

export interface CoverageTarget {
  key: 'LAUNCH' | 'HEALTHY' | 'DEEP';
  label: string;
  perTopic: number;
  /** What reaching it buys, in player terms. */
  meaning: string;
}

export const COVERAGE_TARGETS: CoverageTarget[] = [
  { key: 'LAUNCH', label: 'Launch floor', perTopic: 25, meaning: 'Every topic playable. A casual run of ten matches rarely repeats.' },
  { key: 'HEALTHY', label: 'Healthy', perTopic: 50, meaning: 'A regular player can put fifty matches into a realm without noticing repeats.' },
  { key: 'DEEP', label: 'Deep', perTopic: 100, meaning: 'Survives a dedicated player and leaves room for the hard band to matter.' },
];

/** What one realm needs. Realms differ in size, so the realm has to be named. */
export const realmTarget = (t: CoverageTarget, realm: string) => t.perTopic * topicCount(realm);
/** Across all ten realms, summed rather than multiplied for the same reason. */
export const poolTarget = (t: CoverageTarget) =>
  Object.keys(TOPIC_CORE).reduce((n, r) => n + realmTarget(t, r), 0);

export type TopicTier = 'GATEWAY' | 'STANDARD' | 'DEEP';

/** easy / medium / hard share each tier must generate to. */
export const TIER_MIX: Record<TopicTier, [number, number, number]> = {
  // The pop-culture door into the realm. Whatever the subject, these ask
  // only about facts that reached film, school or the news.
  GATEWAY: [0.60, 0.30, 0.10],
  STANDARD: [0.50, 0.35, 0.15],
  // Where LUMINARY is actually earned. Kept to three topics per realm so
  // the realm-wide blend still lands near 50/35/15.
  DEEP: [0.25, 0.40, 0.35],
};

/** Six per realm. Chosen for reach, not for being easy subjects. */
export const GATEWAY_TOPICS = new Set<string>([
  // Broad anchors, added after the dry run left 20% of questions homeless.
  'Rules of the Game: How Sports Actually Work', 'Athletes Everyone Has Heard Of',
  'Famous Cities & What They Are Known For', 'Animals, Plants & Where They Live',
  'Figures Everyone Has Heard Of', 'Inventions & Firsts That Changed Daily Life',
  'The Everyday Sky: Day, Night, Seasons & Tides', 'Space in Film & Popular Imagination',
  'Monsters & Legends in Popular Culture', 'Symbols, Signs & Lucky Charms',
  'Everyday Science: Why Things Work That Way', 'Plants, Trees & the Green World',
  'Everyday Food: Salads, Sauces & Sides', 'Fruit, Vegetables & Where They Grow',
  'Tech Words & Symbols Everyone Uses', 'Digital Life: Apps, Accounts & Devices',
  'Rock Bands: Line-ups, Albums & Legends', 'Songs Everyone Knows the Words To',
  'Famous Films & Their Characters', 'Theatre, Plays & Stage Classics',
  // ARENA
  'World Cup Finals: Goals, Upsets & Heartbreak', 'Olympic Torch, Ceremonies & Iconic Moments',
  'Sports Nicknames & Retired Numbers', 'Stadiums Everyone Recognises',
  'Sports Movies & the Real Stories Behind Them', 'Underdogs & Miracle Comebacks',
  // ATLAS
  'Capital Cities You Should Know', 'Flags: Colours, Symbols & Stories',
  'Landmarks You Would Recognise Instantly', 'Wonders of the World, Old & New',
  'Rivers That Built Civilisations', 'Islands: Paradise, Prison & Volcano',
  // CHRONICLES
  'Ancient Egypt: Pharaohs, Tombs & Curses', 'Rome: Emperors, Legions & Collapse',
  'Vikings: Raids, Ships & Sagas', 'World War II: The Turning Points',
  'Kings & Queens Who Changed Everything', 'Knights, Castles & the Crusades',
  // COSMOS
  'The Planets: Facts & Freak Weather', 'Moon Landings & the Space Race',
  'The Sun: Flares, Eclipses & Cycles', 'Astronauts & Life in Zero Gravity',
  'Black Holes & What Falls In', 'Constellations & Ancient Sky Maps',
  // ENIGMA
  'Greek Gods & Their Grudges', 'Cryptids: Bigfoot, Nessie & Friends',
  'Ghost Stories That Refuse to Die', 'Curses, Omens & Superstitions Worldwide',
  'Atlantis & Other Lost Lands', 'Hoaxes That Fooled the World',
  // EUREKA
  'The Human Body: Organs, Blood & Bone', 'Dinosaurs: Kings, Feathers & Extinction',
  'Weather: Storms, Fronts & Forecasts', 'Everyday Physics: Why Things Fall & Float',
  'Chemistry in the Kitchen', 'The Animal Kingdom: Senses, Records & Oddities',
  // FEAST
  'National Dishes & Where They Come From', 'Street Food Around the World',
  'Desserts With a Story', 'Bread in Every Culture',
  'Coffee: Beans, Brewing & Ritual', 'Fast Food Empires & How They Grew',
  // NEXUS
  'Smartphone Wars: iPhone vs Android', 'Memes & Internet Culture',
  'Game Consoles Through the Years', 'Phones Before Smartphones',
  'Famous Product Flops', 'Streaming & the Death of the Disc',
  // SOUNDWAVE
  'The Beatles & the British Invasion', 'One-Hit Wonders & Where They Went',
  'Soundtracks That Made the Film', 'Festivals: Woodstock to Glastonbury',
  'Music Video as Art Form (MTV Era)', 'K-Pop & the Global Pop Machine',
  // SPOTLIGHT
  'Oscar Winners & Famous Snubs', 'Disney Animation Through the Decades',
  'Movie Quotes Everyone Knows', 'James Bond: Actors, Cars & Villains',
  'Superhero Films & Their Comic Origins', 'Sitcoms That Defined a Decade',
  // EUREKA, added with the extension. Curie, Franklin, Lovelace and Hopper are
  // school syllabus names, so this one has a genuine popular surface to draw on.
  'Women Who Changed Science',
]);

/** Three per realm. The only place the factory is allowed to go obscure. */
export const DEEP_TOPICS = new Set<string>([
  "Cricket's Great Test Matches", "Golf's Majors & Famous Holes", 'Esports: From Arcades to Arenas',
  'Time Zones, the Equator & the Poles', 'Maps, Explorers & Famous Cartography Mistakes', 'Great Migrations & Human Movement',
  'The Silk Road: Traders, Spices & Ideas', 'Aztec, Maya & Inca', 'Age of Sail: Explorers & Pirates',
  'Dark Matter & Dark Energy', 'Galaxies & the Shape of the Universe', 'Voyager & the Golden Record',
  'Codes & Ciphers Still Unbroken', 'Celtic Folklore & the Fae', 'Dreams, Omens & How Cultures Read Them',
  'Famous Maths Puzzles & Proofs', 'Einstein & the Relativity Revolution', 'Nobel Prizes & the People Behind Them',
  'Cheese: Regions, Moulds & Rules', 'Wine, Beer & Fermentation', 'Cooking Techniques Explained',
  'Programming Languages & Who Made Them', 'Open Source & the Free Software Movement', 'Passwords, Encryption & Staying Safe',
  'Classical Giants: Bach to Beethoven', 'Jazz: New Orleans to Bebop', 'Album Cover Art as Statement',
  'Classic Hollywood: Silents to the Golden Age', 'Documentaries That Changed Minds', 'Cult Films & Midnight Screenings',
  // Added with the CHRONICLES and EUREKA extension. DEEP is for subjects whose
  // famous surface is thin: most players can name something about Rome, few can
  // name anything about Goryeo, so the difficulty budget has to lean harder.
  'Korean Dynasties: Goryeo & Joseon', 'Angkor, Srivijaya & Southeast Asia',
  'Byzantium: The Empire That Outlived Rome', "The Reformation & Europe's Religious Wars",
  'Treaties That Redrew the Map',
  'Particle Physics & the Standard Model', 'Organic Chemistry & Carbon',
  'Thermodynamics: Heat, Work & Entropy', 'Rare Earths & Strategic Materials',
]);

export function tierOfTopic(topic: string): TopicTier {
  if (GATEWAY_TOPICS.has(topic)) return 'GATEWAY';
  if (DEEP_TOPICS.has(topic)) return 'DEEP';
  return 'STANDARD';
}

/**
 * What "easy" means inside a topic, for the generation prompt. This is the
 * instruction that actually equalises the realms: an easy question in a
 * specialist realm is not a watered-down specialist question, it is the one
 * fact from that topic that escaped into general culture.
 */
export const SURFACE_RULE =
  'An EASY question must target a fact from this topic that reached popular ' +
  'culture: something encountered in school, a well-known film, the news, or ' +
  'everyday life. Never a simplified version of a specialist fact. If the topic ' +
  'has no such fact, ask about the single most recognisable name, object or ' +
  'event in it.';
